import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/mock";
import { FadeUp } from "@/components/shared/RevealText";

export default function Testimonials() {
    const [i, setI] = useState(0);
    const t = TESTIMONIALS[i];
    const next = () => setI((v) => (v + 1) % TESTIMONIALS.length);
    const prev = () => setI((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

    return (
        <section className="relative border-t border-ink/10 bg-slate-25 py-24 md:py-32" data-testid="testimonials-section">
            <div className="container-x">
                <div className="mb-16 grid grid-cols-1 items-end gap-8 md:grid-cols-12">
                    <FadeUp className="md:col-span-7">
                        <div className="eyebrow mb-4">Ch. 08 · Testimonials</div>
                        <h2 className="h-display font-display text-[40px] font-black leading-[0.95] text-ink md:text-[64px]">
                            Voices from
                            <br />
                            the <span className="serif-italic text-pink">community</span>.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="md:col-span-5">
                        <p className="max-w-md text-[15px] leading-relaxed text-slate-600 md:text-[16px]">
                            Illustrative quotes from the platform&apos;s early pilot participants across three districts.
                        </p>
                    </FadeUp>
                </div>

                <div className="relative grid grid-cols-1 items-center gap-12 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <div className="relative aspect-square overflow-hidden rounded-[24px] border border-ink/10 bg-white">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={t.id}
                                    src={t.image}
                                    alt={t.name}
                                    initial={{ opacity: 0, scale: 1.05 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 1.02 }}
                                    transition={{ duration: 0.6 }}
                                    className="h-full w-full object-cover"
                                />
                            </AnimatePresence>
                            <div className="pointer-events-none absolute inset-4 border border-white/50" />
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <Quote className="mb-6 h-10 w-10 text-pink" />
                        <AnimatePresence mode="wait">
                            <motion.blockquote
                                key={t.id}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.5 }}
                                className="font-display text-[28px] font-medium leading-tight tracking-tight text-ink md:text-[38px]"
                            >
                                &ldquo;{t.quote}&rdquo;
                            </motion.blockquote>
                        </AnimatePresence>
                        <div className="mt-8 flex items-center justify-between">
                            <div>
                                <div className="font-display text-[16px] font-semibold text-ink">{t.name}</div>
                                <div className="text-[12px] uppercase tracking-[0.24em] text-slate-500">{t.role}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={prev}
                                    className="grid h-11 w-11 place-items-center rounded-full border border-ink/10 text-ink transition-colors hover:border-pink hover:text-pink"
                                    aria-label="Previous"
                                    data-testid="testimonial-prev"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={next}
                                    className="grid h-11 w-11 place-items-center rounded-full border border-ink/10 text-ink transition-colors hover:border-pink hover:text-pink"
                                    aria-label="Next"
                                    data-testid="testimonial-next"
                                >
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
